import { RadialBar, RadialBarChart, ResponsiveContainer, PolarAngleAxis } from "recharts";
import { TrendingUp, TrendingDown } from "lucide-react";
import { fromKm, fmt, unitLabel } from "@/lib/units";
import { colorForVehicle } from "@/lib/vehicleColors";

/**
 * Single-vehicle radial gauge — original "Allowance Projection" presentation.
 * Kept as a self-contained sub-component so the multi-vehicle morph can swap
 * presentation without disturbing the wrapping card frame.
 */
function SingleVehicleRing({ usedKm, limitKm, unit, projection }) {
  const used = fromKm(usedKm || 0, unit);
  const limit = fromKm(limitKm || 0, unit);
  const remaining = Math.max(0, limit - used);
  const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  const overLimit = used > limit;
  const data = [{ name: "used", value: pct, fill: "#E82127" }];
  const u = unitLabel(unit);

  return (
    <>
      <div className="relative flex-1 flex items-center justify-center min-h-[180px] lg:min-h-[200px] ring-sweep">
        <ResponsiveContainer width="100%" height={210}>
          <RadialBarChart
            innerRadius="78%"
            outerRadius="100%"
            data={data}
            startAngle={220}
            endAngle={-40}
            barSize={9}
          >
            <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
            <RadialBar background={{ fill: "var(--border-hover)" }} dataKey="value" cornerRadius={6} />
          </RadialBarChart>
        </ResponsiveContainer>

        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <div className="label-tiny mb-1">Used</div>
          <div className="font-display text-3xl md:text-4xl lg:text-5xl font-light tracking-tighter text-[var(--text-primary)]">
            {fmt(used, 0)}
          </div>
          <div className="text-[11px] text-[var(--text-secondary)] mt-1 font-mono-data">
            of {fmt(limit, 0)} {u}
          </div>
          <div
            className={`mt-3 px-2.5 py-0.5 rounded-full text-[11px] font-mono-data tracking-wider ${
              overLimit
                ? "bg-[#E82127]/15 text-[#FF5A5F] border border-[#E82127]/30"
                : pct > 80
                ? "bg-[#FF9F0A]/10 text-[#FFB340] border border-[#FF9F0A]/20"
                : "bg-[#E82127]/10 text-[#E82127] border border-[#E82127]/20"
            }`}
            data-testid="gauge-pct-badge"
          >
            {pct.toFixed(1)}%
          </div>

          {projection && projection.km > 0 && (
            <div
              className="mt-2 flex items-center gap-1 text-[10px] tracking-wider uppercase"
              data-testid="gauge-projection"
              title={`Based on current daily rate of ${fmt(fromKm(projection.dailyRate, unit), 1)} ${u}/day`}
            >
              {projection.overCap ? (
                <TrendingUp className="w-3 h-3 text-[#FF5A5F]" strokeWidth={2} />
              ) : (
                <TrendingDown className="w-3 h-3 text-[var(--text-muted)]" strokeWidth={2} />
              )}
              <span className="text-[var(--text-muted)]">Projected</span>
              <span
                className={`font-mono-data ${projection.overCap ? "text-[#FF5A5F]" : "text-[var(--text-primary)]"}`}
              >
                {fmt(fromKm(projection.km, unit), 0)} {u}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 pt-3 mt-2 border-t border-[var(--border-hover)]">
        <div>
          <div className="label-tiny">Remaining</div>
          <div className="font-mono-data text-lg lg:text-xl text-[var(--text-primary)] mt-1">
            {fmt(remaining, 0)} <span className="text-xs text-[var(--text-muted)]">{u}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="label-tiny">Annual Cap</div>
          <div className="font-mono-data text-lg lg:text-xl text-[var(--text-primary)] mt-1">
            {fmt(limit, 0)} <span className="text-xs text-[var(--text-muted)]">{u}</span>
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Multi-vehicle stacked horizontal bar chart — one row per vehicle, each
 * showing usage vs. annual cap. Lives INSIDE the same gauge card frame so
 * the dashboard grid stays visually stable when the user toggles the
 * vehicle dropdown.
 */
function MultiVehicleStacked({ vehicles, perVehicleStats, unit, totalUsedKm, totalLimitKm }) {
  const u = unitLabel(unit);
  const totalUsed = fromKm(totalUsedKm || 0, unit);
  const totalLimit = fromKm(totalLimitKm || 0, unit);
  const totalPct = totalLimit > 0 ? Math.min(100, (totalUsed / totalLimit) * 100) : 0;

  return (
    <>
      <div className="flex-1 flex flex-col justify-center min-h-[180px] lg:min-h-[200px] gap-3 py-2">
        {vehicles.map((v) => {
          const c = colorForVehicle(v.id, vehicles);
          const st = perVehicleStats[v.id];
          const usedKm = st?.year_used_km || 0;
          const limitKm = v.yearly_limit_km || 0;
          const usedDisp = fromKm(usedKm, unit);
          const limitDisp = fromKm(limitKm, unit);
          const pct = limitKm > 0 ? Math.min(100, (usedKm / limitKm) * 100) : 0;
          const over = usedKm > limitKm && limitKm > 0;

          return (
            <div key={v.id} data-testid={`gauge-vehicle-row-${v.id}`}>
              <div className="flex items-center justify-between mb-1.5 gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="inline-block w-2 h-2 shrink-0"
                    style={{ background: c.fill, borderRadius: 1 }}
                  />
                  <span className="text-[11px] tracking-tight text-[var(--text-primary)] truncate">{v.name}</span>
                </div>
                <span
                  className={`text-[10px] font-mono-data ${over ? "text-[#FF5A5F]" : "text-[var(--text-secondary)]"}`}
                >
                  {pct.toFixed(1)}%
                </span>
              </div>
              <div
                className="relative h-2 bg-[var(--bg-emphasis)] overflow-hidden"
                style={{ borderRadius: 1 }}
              >
                <div
                  className="h-full transition-all duration-700 ease-out bar-grow"
                  style={{
                    width: `${pct}%`,
                    background: over ? "#E82127" : c.fill,
                  }}
                />
              </div>
              <div className="flex justify-between mt-1 text-[10px] text-[var(--text-muted)] font-mono-data">
                <span>{fmt(usedDisp, 0)} {u}</span>
                <span>of {fmt(limitDisp, 0)} {u}</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-2 gap-4 pt-3 mt-2 border-t border-[var(--border-hover)]">
        <div>
          <div className="label-tiny">Fleet Used</div>
          <div className="font-mono-data text-lg lg:text-xl text-[var(--text-primary)] mt-1">
            {fmt(totalUsed, 0)} <span className="text-xs text-[var(--text-muted)]">{u}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="label-tiny">Fleet Cap</div>
          <div className="font-mono-data text-lg lg:text-xl text-[var(--text-primary)] mt-1">
            {fmt(totalLimit, 0)} <span className="text-xs text-[var(--text-muted)]">{u}</span>
            <span className="text-[10px] text-[var(--text-muted)] ml-2">· {totalPct.toFixed(0)}%</span>
          </div>
        </div>
      </div>
    </>
  );
}

export default function ProgressGauge({
  usedKm,
  limitKm,
  unit,
  vehicleName,
  year,
  projection,
  isMultiAll = false,
  morphCapable = false,
  vehicles = [],
  perVehicleStats = {},
}) {
  const multi = isMultiAll && vehicles.length > 1;
  const headerLabel = multi ? `${vehicles.length} Vehicles` : vehicleName || "All";

  // Pin a min-height when the household is morph-capable (>1 vehicle) so the
  // card dimensions stay identical when the user toggles between All and a
  // single vehicle via the dropdown. Single-vehicle households keep their
  // natural compact height.
  const stableStyle = morphCapable ? { minHeight: 420 } : undefined;

  return (
    <div
      className="bg-[var(--bg-elevated)] border border-[var(--border-hover)] rounded-lg p-5 h-full flex flex-col card-hover"
      style={stableStyle}
      data-testid="progress-gauge"
      data-mode={multi ? "multi" : "single"}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="label-tiny">Yearly Limit · {year}</span>
        <span className="label-tiny truncate max-w-[55%] text-right">{headerLabel}</span>
      </div>

      {multi ? (
        <MultiVehicleStacked
          vehicles={vehicles}
          perVehicleStats={perVehicleStats}
          unit={unit}
          totalUsedKm={usedKm}
          totalLimitKm={limitKm}
        />
      ) : (
        <SingleVehicleRing usedKm={usedKm} limitKm={limitKm} unit={unit} projection={projection} />
      )}
    </div>
  );
}
